// Customers.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const money = (n) => Number(n || 0).toFixed(2);
const digitsOnly = (p) => String(p || '').replace(/\D/g, '');
const norm = (v) => (v == null ? '' : String(v)).toLowerCase();

/* ===================== Indian phone helpers =====================
Accept inputs like:
- Mobile: XXXXXXXXXX / 0XXXXXXXXXX / 91XXXXXXXXXX / +91XXXXXXXXXX
- Landline: 0<STD><SUB> or +91<STD><SUB> (STD 2–4, SUB 6–8), total 8–10 digits after trunk/country removal

We compare duplicates by NSN (national significant number), i.e., after removing 0091/91 and any leading 0s.
=================================================================*/

function stripCountryOrTrunk(raw) {
  let s = String(raw || '').replace(/\D/g, '');

  // Remove international or country code only when longer than NSN
  if (s.startsWith('0091')) s = s.slice(4);
  else if (s.length > 10 && s.startsWith('91')) s = s.slice(2);

  // Remove any trunk 0(s) (handles 0 / 00 / 000…)
  while (s.startsWith('0')) s = s.slice(1);

  return s; // NSN
}

function phoneKey(raw) {
  return stripCountryOrTrunk(raw); // stable key for equality/sort
}

function classifyINPhone(raw) {
  const only = digitsOnly(raw);
  if (!only) return { ok: false, type: null, nsn: '', e164: '', reason: 'Empty' };

  const nsn = stripCountryOrTrunk(only);

  // Mobile: exactly 10 digits, starts [6-9]
  if (/^[6-9]\d{9}$/.test(nsn)) return { ok: true, type: 'mobile', nsn, e164: `+91${nsn}` };

  // Landline: 8–10 digits after removing prefixes/trunk 0s
  if (/^\d{8,10}$/.test(nsn)) return { ok: true, type: 'landline', nsn, e164: `+91${nsn}` };

  return { ok: false, type: null, nsn, e164: '', reason: 'Invalid' };
}

function isValidEmail(email) {
  const e = String(email || '').trim();
  if (!e) return true; // optional
  return /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(e);
}

/* ===================== UI bits ===================== */

const TH = ({ label, col, sort, onSort }) => (
  <th
    role="button"
    tabIndex={0}
    aria-sort={sort.key === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    onClick={() => onSort(col)}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort(col)}
    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
  >
    {label}
    {sort.key === col && (
      <span className="mono" style={{ marginLeft: 6 }}>
        {sort.dir === 'asc' ? '▲' : '▼'}
      </span>
    )}
  </th>
);

async function hasAnySales(customerId) {
  try {
    const r = await api.get('/sales', { params: { pageSize: 500, sort: 'date_desc' } });
    const rows = Array.isArray(r.data) ? r.data : [];
    return rows.some((s) => Number(s.customer_id || 0) === Number(customerId));
  } catch {
    return true; // conservative
  }
}

function Drawer({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1000 }} />
      <aside
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: 0, right: 0, bottom: 0,
          width: 'min(520px,100%)',
          background: 'var(--bg,#fff)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 0 0 1px var(--border,#e5e7eb), -8px 0 24px rgba(0,0,0,.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border,#e5e7eb)' }}>
          <div className="flex" style={{ alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>{title}</h3>
            <button className="btn btn-sm secondary" onClick={onClose} style={{ marginLeft: 'auto' }}>✕</button>
          </div>
        </div>
        <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>{children}</div>
        {footer && <div style={{ padding: 12, borderTop: '1px solid var(--border,#e5e7eb)' }}>{footer}</div>}
      </aside>
    </>
  );
}

/* ===================== Info Modal ===================== */

function InfoModal({ open, title, content, onClose }) {
  if (!open) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.35)',
          zIndex: 2000
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--bg, #fff)',
          zIndex: 2001,
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxWidth: '400px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ margin: 0 }}>{title}</h4>
          <button
            className="btn btn-sm secondary"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--text, #333)' }}>
          {content}
        </div>
      </div>
    </>
  );
}

/* ===================== Main Component ===================== */

export default function Customers() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', state_code: '', gstin: '', tags: '', store_credit: '',
  });

  const [initialStateCode, setInitialStateCode] = useState('');
  const [stateLocked, setStateLocked] = useState(false);
  const [infoOpen, setInfoOpen] = useState(null); // 'store_credit' or 'state_code'

  async function load(q = '') {
    setLoading(true);
    try {
      const resp = await api.get('/customers' + (q ? `?search=${encodeURIComponent(q)}` : ''));
      setList(resp.data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const total = list?.length || 0;
  const totalCredit = useMemo(() => list.reduce((s, c) => s + Number(c.store_credit || 0), 0), [list]);
  const tagsArr = useMemo(() => (form.tags || '').split(',').map((s) => s.trim()).filter(Boolean), [form.tags]);

  // PHONE + EMAIL validation state
  const phoneInfo = useMemo(() => classifyINPhone(form.phone), [form.phone]);
  const emailOk  = useMemo(() => isValidEmail(form.email), [form.email]);

  // Duplicate check by normalized NSN; allow matching own record while editing
  const dupPhone = useMemo(() => {
    const key = phoneKey(form.phone);
    if (!key) return false;
    return (list || []).some((c) => phoneKey(c.phone || '') === key && (!editId || c.id !== editId));
  }, [form.phone, list, editId]);

  // Save disabled until all checks pass
  const canSave = useMemo(() => {
    if (!form.name.trim()) return false;
    if (!phoneInfo.ok) return false;
    if (dupPhone) return false;
    if (!emailOk) return false;
    return true;
  }, [form.name, phoneInfo.ok, dupPhone, emailOk]);

  function onChange(e) {
    const { name, value } = e.target;
    if (name === 'phone') return setForm((f) => ({ ...f, phone: value })); // keep as-typed
    if (name === 'state_code') return setForm((f) => ({ ...f, state_code: String(value || '').toUpperCase() }));
    if (name === 'gstin') return setForm((f) => ({ ...f, gstin: String(value || '').toUpperCase() }));
    if (name === 'store_credit') {
      // ---- Minimal fix: allow clearing the field while typing ----
      // When the input is temporarily empty (or just a '.'), don't coerce to Number(…) → 0
      if (value === '' || value === '.') {
        return setForm((f) => ({ ...f, store_credit: '' }));
      }
      const num = Number(value);
      // ignore invalid input (keeps previous good value)
      if (Number.isNaN(num)) return;
      return setForm((f) => ({ ...f, store_credit: num }));
    }
    setForm((f) => ({ ...f, [name]: value }));
  }

  function openNew() {
    setEditId(null);
    setErr('');
    setForm({ name: '', phone: '', email: '', address: '', state_code: '', gstin: '', tags: '', store_credit: '' });
    setInitialStateCode('');
    setStateLocked(false);
    setDrawerOpen(true);
  }

  function openEdit(c) {
    setEditId(c.id);
    setErr('');
    setForm({
      name: c.name || '',
      phone: c.phone || '',
      email: c.email || '',
      address: c.address || '',
      state_code: (c.state_code || '').toUpperCase(),
      gstin: (c.gstin || '').toUpperCase(),
      tags: Array.isArray(c.tags) ? c.tags.join(', ') : (c.tags || ''),
      store_credit: c.store_credit != null ? Number(c.store_credit) : 0,
    });
    setInitialStateCode((c.state_code || '').toUpperCase());
    hasAnySales(c.id).then((hasSales) => setStateLocked(!!hasSales));
    setDrawerOpen(true);
  }

  function closeDrawer() { setDrawerOpen(false); }

  async function onDelete(id) {
    if (!window.confirm('Delete this customer?')) return;
    await api.delete(`/customers/${id}`);
    await load(search);
    if (editId === id) closeDrawer();
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (saving) return;
    setErr('');

    const sc = String(form.state_code || '').toUpperCase();
    if (sc && !/^[A-Z]{2}$/.test(sc)) return setErr('Enter a valid 2-letter state code (e.g., OD, KA).');
    if (editId && stateLocked && sc !== initialStateCode)
      return setErr('State code is locked after the first sale. Create a new customer to change.');

    if (!phoneInfo.ok) return setErr('Enter a valid Indian phone number (mobile or landline).');
    if (dupPhone)     return setErr('A customer with this phone already exists.');
    if (!emailOk)     return setErr('Enter a valid email address or leave it blank.');

    // Canonicalize before save
    const payload = {
      ...form,
      phone: phoneInfo.e164, // +91… for both mobile and landline
      state_code: sc,
      tags: tagsArr,
      store_credit: Math.max(0, Number(form.store_credit || 0)),
    };

    try {
      setSaving(true);
      if (editId) await api.put(`/customers/${editId}`, payload);
      else        await api.post('/customers', payload);
      closeDrawer();
      await load(search);
    } catch {
      setErr('Save failed');
    } finally {
      setSaving(false);
    }
  }

  const sorted = useMemo(() => {
    const arr = Array.isArray(list) ? [...list] : [];
    const { key, dir } = sort;
    const val = (c) => {
      switch (key) {
        case 'phone':  return phoneKey(c.phone || '');
        case 'email':  return norm(c.email);
        case 'gstin':  return norm(c.gstin);
        case 'credit': return Number(c.store_credit || 0);
        case 'name':
        default:       return norm(c.name);
      }
    };
    arr.sort((a, b) => {
      let cmp = val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0;
      return dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [list, sort]);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = Number(localStorage.getItem('customers.rowsPerPage'));
    return [10, 25, 50, 100].includes(v) ? v : 25;
  });
  useEffect(() => setPage(1), [search]);
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil((sorted.length || 0) / rowsPerPage));
    if (page > maxPage) setPage(maxPage);
  }, [sorted, rowsPerPage, page]);
  const pageCount = Math.max(1, Math.ceil((sorted.length || 0) / rowsPerPage));
  const startIdx = (page - 1) * rowsPerPage;
  const pageRows = useMemo(() => sorted.slice(startIdx, startIdx + rowsPerPage), [sorted, startIdx, rowsPerPage]);
  const endIdx = startIdx + rowsPerPage;
  const gotoFirst = () => setPage(1);
  const gotoPrev  = () => setPage(p => Math.max(1, p - 1));
  const gotoNext  = () => setPage(p => Math.min(pageCount, p + 1));
  const gotoLast  = () => setPage(pageCount);

  return (
    <div className="container" style={{ width:'100%', boxSizing:'border-box' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <h2 style={{ marginRight: 'auto' }}>Customers</h2>
        <div className="muted" style={{ whiteSpace: 'nowrap' }}>
          Total: <b>{total}</b> • Credit: <b>₹ {money(totalCredit)}</b>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, margin: '8px 0 12px 0', flexWrap:'wrap' }}>
        <input
          className="input"
          placeholder="Search by name, phone, email, GSTIN"
          style={{ flex: 1 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search ? <button className="btn secondary" onClick={() => setSearch('')}>Clear</button> : null}
        <button className="btn" onClick={() => openNew()}>+ Add</button>
      </div>

      <div className="card" style={{ overflowX:'auto' }}>
        <div className="flex" style={{ alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0 }}>List</h3>
          {loading && <span className="muted" style={{ marginLeft: 8 }}>Loading…</span>}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <TH label="Name" col="name" sort={sort} onSort={toggleSort} />
                <TH label="Phone" col="phone" sort={sort} onSort={toggleSort} />
                <TH label="Email" col="email" sort={sort} onSort={toggleSort} />
                <TH label="GSTIN" col="gstin" sort={sort} onSort={toggleSort} />
                <TH label="Credit" col="credit" sort={sort} onSort={toggleSort} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{c.phone}</td>
                  <td>{c.email}</td>
                  <td>{c.gstin}</td>
                  <td>₹ {money(c.store_credit)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm" onClick={() => openEdit(c)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => onDelete(c.id)} style={{ marginLeft: 8 }}>Delete</button>
                  </td>
                </tr>
              ))}
              {!sorted.length && !loading && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 16 }}>No customers</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex" style={{
          alignItems: 'center',
          gap: 12,
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderTop: '1px solid var(--border,#e5e7eb)',
          flexWrap: 'wrap'
        }}>
          <div className="flex" style={{ alignItems: 'center', gap: 8 }}>
            <label className="muted" htmlFor="rows-per-page">Rows/page</label>
            <select
              id="rows-per-page"
              className="input"
              value={rowsPerPage}
              onChange={(e) => { const v = Number(e.target.value || 25); localStorage.setItem('customers.rowsPerPage', String(v)); setRowsPerPage(v); setPage(1); }}
              style={{ width: 96 }}
              aria-label="Rows per page"
            >
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>

            <span className="muted" style={{ marginLeft: 12 }}>
              {sorted.length ? `${startIdx + 1}–${Math.min(endIdx, sorted.length)} of ${sorted.length}` : '0 of 0'}
            </span>
          </div>

          <div className="flex" style={{ alignItems: 'center', gap: 6 }}>
            <button className="btn btn-sm secondary" onClick={gotoFirst} disabled={page <= 1} aria-label="First page">«</button>
            <button className="btn btn-sm secondary" onClick={gotoPrev}  disabled={page <= 1} aria-label="Previous page">‹</button>
            <span className="muted" style={{ padding: '0 6px' }}>Page <b>{page}</b> / {pageCount}</span>
            <button className="btn btn-sm secondary" onClick={gotoNext}  disabled={page >= pageCount} aria-label="Next page">›</button>
            <button className="btn btn-sm secondary" onClick={gotoLast}  disabled={page >= pageCount} aria-label="Last page">»</button>
          </div>
        </div>
      </div>

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title={editId ? 'Edit Customer' : 'Add Customer'}
        footer={
          <div className="flex" style={{ gap: 8, justifyContent: 'flex-end' }}>
            {editId && <button className="btn btn-danger" type="button" onClick={() => onDelete(editId)}>Delete</button>}
            <button className="btn btn-secondary" type="button" onClick={closeDrawer}>Cancel</button>
            <button className="btn" form="customer-form" type="submit" disabled={saving || !canSave}>
              {saving ? 'Saving…' : (editId ? 'Update' : 'Save')}
            </button>
          </div>
        }
      >
        {err && <div className="alert alert-error" style={{ marginBottom: 8 }}>{err}</div>}

        <form id="customer-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 10 }}>
          <div>
            <label className="muted" style={{ fontSize: 12 }}>Name *</label>
            <input className="input" name="name" placeholder="Full name"
                   value={form.name} onChange={onChange} required />
          </div>

          <div className="auto-grid-2">
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Phone *</label>
              <input className="input" name="phone" placeholder="Mobile or landline (India)"
                     value={form.phone} onChange={onChange} required
                     aria-invalid={!phoneInfo.ok || dupPhone} />
              {!phoneInfo.ok && form.phone.trim() && (
                <div className="muted" style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>
                  Enter a valid Indian phone (mobile 10 digits starting 6–9, or landline 8–10 digits with STD).
                </div>
              )}
              {dupPhone && (
                <div className="muted" style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>
                  Duplicate phone found (same number with 0/91/+91).
                </div>
              )}
              {phoneInfo.ok && (
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Will save as <b>{phoneInfo.e164}</b> ({phoneInfo.type})
                </div>
              )}
            </div>

            <div>
              <label className="muted" style={{ fontSize: 12 }}>Email</label>
              <input className="input" name="email" placeholder="name@example.com"
                     value={form.email} onChange={onChange} aria-invalid={!emailOk} />
              {!emailOk && (
                <div className="muted" style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>
                  Enter a valid email or leave it blank.
                </div>
              )}
            </div>
          </div>

          <div className="auto-grid-2">
            <div>
              <label className="muted" style={{ fontSize: 12 }}>GSTIN</label>
              <input className="input" name="gstin" placeholder="e.g., 22AAAAA0000A1Z5"
                     value={form.gstin} onChange={onChange} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <label className="muted" style={{ fontSize: 12 }}>State Code</label>
                <button
                  type="button"
                  className="btn btn-sm secondary"
                  onClick={() => setInfoOpen('state_code')}
                  style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
                  title="Learn more about State Code"
                >
                  ℹ️
                </button>
              </div>
              <input className="input" name="state_code" placeholder="Enter state code (e.g., OD, KA)."
                     value={(form.state_code || '').toUpperCase()}
                     onChange={(e) => setForm((f) => ({ ...f, state_code: e.target.value.toUpperCase() }))}
                     disabled={!!editId && stateLocked}
                     title={ editId ? (stateLocked ? 'Locked: customer has at least one sale'
                                                   : 'Editable: no sales yet (correct typos)')
                                    : 'Enter 2-letter state code' }
                     maxLength={2} />
              {editId && (
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {stateLocked ? 'State code is locked after the first sale.'
                               : 'Default state code is from shop settings. No sales found yet — you can still correct the state.'}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="muted" style={{ fontSize: 12 }}>Address</label>
            <textarea className="input" name="address" placeholder="Street, City, PIN"
                      value={form.address} onChange={onChange} />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <label className="muted" style={{ fontSize: 12 }}>Store Credit (₹)</label>
              <button
                type="button"
                className="btn btn-sm secondary"
                onClick={() => setInfoOpen('store_credit')}
                style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
                title="Learn more about Store Credit"
              >
                ℹ️
              </button>
            </div>
            <input
              className="input mono"
              type="number"
              min="0"
              step="0.01"
              name="store_credit"
              placeholder="0.00"
              value={form.store_credit}
              onChange={onChange}
            />
          </div>
        </form>
      </Drawer>

      <InfoModal
        open={infoOpen === 'state_code'}
        title="State Code"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>State Code</strong> is a 2-letter abbreviation used to identify Indian states for GST and tax purposes.</p>
            <p><strong>Examples:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>OD = Odisha</li>
              <li>KA = Karnataka</li>
              <li>MH = Maharashtra</li>
              <li>DL = Delhi</li>
              <li>GJ = Gujarat</li>
            </ul>
            <p><strong>Important:</strong> Once a customer has a sale, the state code becomes locked and cannot be changed. Choose carefully!</p>
            <p>If no state code is specified, the default state from your shop settings will be used.</p>
          </div>
        }
      />

      <InfoModal
        open={infoOpen === 'store_credit'}
        title="Store Credit (₹)"
        onClose={() => setInfoOpen(null)}
        content={
          <div>
            <p><strong>Store Credit</strong> is a prepaid balance that customers can use to make purchases. Think of it as store vouchers or gift cards.</p>
            <p><strong>How it works:</strong></p>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>You can manually add store credit to a customer's account</li>
              <li>When a customer makes a purchase, the amount is deducted from their store credit</li>
              <li>If store credit is insufficient, the customer must pay the remaining balance in cash or another method</li>
              <li>If Customer returns items, the returned amount is added back to their store credit</li>
            </ul>
            <p><strong>Example:</strong> If a customer has ₹500 store credit and makes a ₹300 purchase, their remaining balance becomes ₹200.</p>
          </div>
        }
      />
    </div>
  );
}
